import * as React from "react";
import {
    Box,
    Chip,
    ClickAwayListener,
    Paper,
    Popper,
    Typography,
} from "@mui/material";
import { Close, ExpandLess, ExpandMore } from "@mui/icons-material";
import { StyledMenuItemContainer } from "../StyledComponents/styled";

const RED = "#AF3241";

// Turns "a, b" into ["a", "b"]. Callers can replace this when a field splits differently.
const splitCommaSeparatedInput = (input) =>
    String(input || "")
        .split(",")
        .map((value) => value.trim())
        .filter((value) => value !== "");

// A suggestion can resolve to a list of strings, or to { values, action }.
// "action" tells the caller whether the user picked an existing option or typed a new one.
const valuesFromResolvedOption = (resolved) => {
    if (Array.isArray(resolved)) {
        return { values: resolved, action: "selectOption" };
    }
    if (resolved && Array.isArray(resolved.values)) {
        return {
            values: resolved.values,
            action: resolved.action || "selectOption",
        };
    }
    return { values: [], action: "selectOption" };
};

/**
 * Text field that stores many values as chips and grows when those chips wrap.
 * Pass `options` to show a suggestion list. Leave it unset for free-text only,
 * which is what the standalone Listing Check form needs.
 * `options` may be a fixed list, or a function of the current input text.
 */
const ChipInputField = ({
    value,
    onChange,
    label,
    fieldError,
    inputId,
    inputName,
    options,
    resolveOption,
    isSuggestionMuted,
    noOptionsText = "No matches found",
    parseInput = splitCommaSeparatedInput,
}) => {
    const anchorRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const optionRefs = React.useRef({});
    // A random name stops the browser from offering saved emails or other autofill.
    const browserAutocompleteName = React.useRef(
        `${inputName || "chip_input"}_${Math.random().toString(36).slice(2)}`
    );

    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState("");
    const [isFocused, setIsFocused] = React.useState(false);
    const [highlightedIndex, setHighlightedIndex] = React.useState(-1);

    const selectedValues = React.useMemo(
        () => (Array.isArray(value) ? value : []),
        [value]
    );

    // Undefined options means this field is free-text only: no chevron, no menu.
    const showSuggestions = typeof options !== "undefined";

    const suggestionOptions = React.useMemo(() => {
        if (!showSuggestions) return [];
        if (typeof options === "function") return options(inputValue) || [];
        if (!Array.isArray(options)) return [];

        const query = inputValue.trim().toLowerCase();
        return options.filter((option) =>
            String(option).toLowerCase().includes(query)
        );
    }, [inputValue, options, showSuggestions]);

    const shouldShrinkLabel =
        isFocused ||
        (showSuggestions && open) ||
        inputValue.length > 0 ||
        selectedValues.length > 0;

    React.useEffect(() => {
        setHighlightedIndex(-1);
    }, [inputValue]);

    const closeDropdown = React.useCallback(() => {
        setOpen(false);
        setHighlightedIndex(-1);
    }, []);

    const commitChange = React.useCallback(
        (event, nextValue, action) => {
            onChange(event, nextValue, action);
            setInputValue("");
            closeDropdown();
        },
        [closeDropdown, onChange]
    );

    const focusInput = React.useCallback(() => {
        setTimeout(() => inputRef.current?.focus(), 0);
    }, []);

    const openSuggestions = React.useCallback(() => {
        if (!showSuggestions) return;
        setOpen(true);
    }, [showSuggestions]);

    // A highlighted row can become several stored values. Email uses this to
    // turn "New: a@b.com, c@d.com" into two addresses.
    const selectOption = React.useCallback(
        (event, option) => {
            if (!option) return;
            const resolved = typeof resolveOption === "function"
                ? resolveOption(option)
                : [option];
            const { values: nextValues, action } = valuesFromResolvedOption(resolved);
            if (nextValues.length === 0) return;

            commitChange(event, [...selectedValues, ...nextValues], action);
            focusInput();
        },
        [commitChange, focusInput, resolveOption, selectedValues]
    );

    const createOption = React.useCallback(
        (event) => {
            const parsedValues = parseInput(inputValue);
            if (parsedValues.length === 0) return;
            commitChange(event, [...selectedValues, ...parsedValues], "createOption");
            focusInput();
        },
        [commitChange, focusInput, inputValue, parseInput, selectedValues]
    );

    const removeOptionAtIndex = React.useCallback(
        (event, optionIndex) => {
            event.stopPropagation();
            const nextValue = selectedValues.filter((_, index) => index !== optionIndex);
            commitChange(event, nextValue, "removeOption");
            focusInput();
        },
        [commitChange, focusInput, selectedValues]
    );

    const clearSelection = React.useCallback(
        (event) => {
            event.stopPropagation();
            commitChange(event, [], "clear");
            focusInput();
        },
        [commitChange, focusInput]
    );

    const scrollHighlightedIntoView = React.useCallback((index) => {
        const element = optionRefs.current[`option-${index}`];
        if (element) element.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, []);

    const handleKeyDown = React.useCallback(
        (event) => {
            switch (event.key) {
                case "ArrowDown":
                    if (!showSuggestions) break;
                    event.preventDefault();
                    setOpen(true);
                    if (suggestionOptions.length === 0) return;
                    setHighlightedIndex((prev) => {
                        const next = prev < suggestionOptions.length - 1 ? prev + 1 : 0;
                        setTimeout(() => scrollHighlightedIntoView(next), 0);
                        return next;
                    });
                    break;

                case "ArrowUp":
                    if (!showSuggestions) break;
                    event.preventDefault();
                    setOpen(true);
                    if (suggestionOptions.length === 0) return;
                    setHighlightedIndex((prev) => {
                        const next = prev > 0 ? prev - 1 : suggestionOptions.length - 1;
                        setTimeout(() => scrollHighlightedIntoView(next), 0);
                        return next;
                    });
                    break;

                case "Enter":
                    // A highlighted suggestion wins. Otherwise Enter stores the typed text.
                    if (
                        showSuggestions &&
                        highlightedIndex >= 0 &&
                        highlightedIndex < suggestionOptions.length
                    ) {
                        event.preventDefault();
                        selectOption(event, suggestionOptions[highlightedIndex]);
                    } else if (inputValue.trim() !== "") {
                        event.preventDefault();
                        createOption(event);
                    }
                    break;

                case "Backspace":
                    // An empty input removes the last chip, same as a typical chip field.
                    if (inputValue === "" && selectedValues.length > 0) {
                        event.preventDefault();
                        const nextValue = selectedValues.slice(0, -1);
                        commitChange(event, nextValue, "removeOption");
                    }
                    break;

                case "Escape":
                    if (!open) break;
                    event.preventDefault();
                    closeDropdown();
                    break;

                default:
                    break;
            }
        },
        [
            closeDropdown,
            commitChange,
            createOption,
            highlightedIndex,
            inputValue,
            open,
            scrollHighlightedIntoView,
            selectOption,
            selectedValues,
            showSuggestions,
            suggestionOptions,
        ]
    );

    // Pasting "111, 222" should add both chips immediately, not leave the commas in the box.
    const handlePaste = (event) => {
        const pasted = event.clipboardData.getData("text");
        if (!pasted.includes(",")) return;
        event.preventDefault();
        const parsedValues = parseInput(pasted);
        if (parsedValues.length === 0) return;
        commitChange(event, [...selectedValues, ...parsedValues], "createOption");
        focusInput();
    };

    return (
        <ClickAwayListener onClickAway={closeDropdown}>
            <Box sx={{ width: "100%", position: "relative" }}>
                <Box
                    ref={anchorRef}
                    role={showSuggestions ? "combobox" : undefined}
                    aria-expanded={showSuggestions ? open : undefined}
                    aria-haspopup={showSuggestions ? "listbox" : undefined}
                    onClick={() => {
                        openSuggestions();
                        focusInput();
                    }}
                    sx={{
                        position: "relative",
                        pt: 2.1,
                        // The underline sits on this box, so it moves down as chips wrap to new rows.
                        borderBottom: `1px solid ${RED}`,
                        cursor: "text",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            bottom: -1,
                            width: isFocused || (showSuggestions && open) ? "100%" : "0%",
                            height: "2px",
                            backgroundColor: RED,
                            transition: "width 180ms ease",
                        },
                    }}
                >
                    <Box
                        onClick={(event) => {
                            event.stopPropagation();
                            focusInput();
                        }}
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: shouldShrinkLabel ? 0 : 22,
                            fontSize: shouldShrinkLabel ? 12 : 14,
                            color: RED,
                            transformOrigin: "top left",
                            transition: "all 160ms ease",
                            zIndex: 1,
                        }}
                    >
                        {label}
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            minHeight: 32,
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 0.5,
                                py: 0.5,
                            }}
                        >
                            {selectedValues.map((option, index) => (
                                <Chip
                                    key={`${option}-${index}`}
                                    variant="filled"
                                    label={option}
                                    size="small"
                                    onDelete={(event) => removeOptionAtIndex(event, index)}
                                    sx={{
                                        backgroundColor: RED,
                                        color: "#FFFFFF",
                                        "& .MuiChip-deleteIcon": {
                                            color: "#FFFFFF",
                                        },
                                    }}
                                />
                            ))}
                            <Box
                                component="input"
                                ref={inputRef}
                                id={inputId}
                                name={browserAutocompleteName.current}
                                type="text"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck={false}
                                aria-autocomplete={showSuggestions ? "list" : "none"}
                                data-lpignore="true"
                                data-1p-ignore="true"
                                data-bwignore="true"
                                data-form-type="other"
                                data-field-name={inputName}
                                value={inputValue}
                                onFocus={() => {
                                    setIsFocused(true);
                                    openSuggestions();
                                }}
                                onBlur={() => setIsFocused(false)}
                                onChange={(event) => {
                                    setInputValue(event.target.value);
                                    openSuggestions();
                                }}
                                onKeyDown={handleKeyDown}
                                onPaste={handlePaste}
                                sx={{
                                    flex: "1 1 140px",
                                    minWidth: 80,
                                    border: 0,
                                    outline: "none",
                                    color: RED,
                                    font: "inherit",
                                    fontSize: "13px",
                                    backgroundColor: "transparent",
                                    p: 0,
                                    lineHeight: "24px",
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                flex: "0 0 auto",
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            {selectedValues.length > 0 && (
                                <Close
                                    fontSize="small"
                                    onClick={clearSelection}
                                    sx={{ cursor: "pointer", fontSize: 18 }}
                                />
                            )}
                            {showSuggestions && (
                                open ? (
                                    <ExpandLess
                                        fontSize="small"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            closeDropdown();
                                        }}
                                        sx={{ cursor: "pointer" }}
                                    />
                                ) : (
                                    <ExpandMore
                                        fontSize="small"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setOpen(true);
                                            focusInput();
                                        }}
                                        sx={{ cursor: "pointer" }}
                                    />
                                )
                            )}
                        </Box>
                    </Box>
                </Box>

                {fieldError && (
                    <Typography
                        variant="caption"
                        sx={{ color: RED, mt: 0.5, display: "block" }}
                    >
                        {fieldError}
                    </Typography>
                )}

                {showSuggestions && (
                    <Popper
                        open={open}
                        anchorEl={anchorRef.current}
                        placement="bottom-start"
                        sx={{ zIndex: 1400 }}
                        style={{
                            width: anchorRef.current?.offsetWidth,
                        }}
                    >
                        <Paper
                            role="listbox"
                            sx={{
                                maxHeight: 220,
                                overflowY: "auto",
                                overflowX: "hidden",
                                mt: 0.5,
                            }}
                            onMouseDown={(event) => event.preventDefault()}
                        >
                            {suggestionOptions.length > 0 ? (
                                suggestionOptions.map((option, index) => {
                                    const muted = typeof isSuggestionMuted === "function"
                                        && isSuggestionMuted(option);
                                    return (
                                        <StyledMenuItemContainer
                                            key={`${option}-${index}`}
                                            ref={(element) => {
                                                optionRefs.current[`option-${index}`] = element;
                                            }}
                                            role="option"
                                            onClick={(event) => selectOption(event, option)}
                                            onMouseEnter={() => setHighlightedIndex(index)}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                backgroundColor:
                                                    highlightedIndex === index
                                                        ? "rgba(175, 50, 65, 0.08)"
                                                        : "transparent",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    whiteSpace: "normal",
                                                    wordBreak: "break-word",
                                                    overflowWrap: "break-word",
                                                    color: muted ? RED : "inherit",
                                                }}
                                            >
                                                {option}
                                            </Typography>
                                        </StyledMenuItemContainer>
                                    );
                                })
                            ) : (
                                <Box sx={{ p: 2, textAlign: "center" }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {noOptionsText}
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Popper>
                )}
            </Box>
        </ClickAwayListener>
    );
};

export default ChipInputField;
